import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/Card';
import { format } from 'date-fns';
import { AlertCircle, AlertTriangle, CheckCircle, Clock, Archive, Eye, EyeOff } from 'lucide-react';
import { SyncLog, JobExecutionLog } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const SYNC_LOGS_LOOKBACK_DAYS = 90;

export default function SyncLogs() {
  const [selectedLog, setSelectedLog] = React.useState<SyncLog | null>(null);
  const [showArchived, setShowArchived] = React.useState(false);
  const queryClient = useQueryClient();

  // Filtering, sorting, and pagination state for Sync Logs
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [typeFilter, setTypeFilter] = React.useState<string | null>(null);
  const [sortColumn, setSortColumn] = React.useState<string>('start_time');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [hasMoreSyncLogs, setHasMoreSyncLogs] = React.useState(false);

  // Filtering, sorting, and pagination state for Job Execution Logs
  const [jobStatusFilter, setJobStatusFilter] = React.useState<string | null>(null);
  const [jobTypeFilter, setJobTypeFilter] = React.useState<string | null>(null);
  const [jobSortColumn, setJobSortColumn] = React.useState<string>('start_time');
  const [jobSortDirection, setJobSortDirection] = React.useState<'asc' | 'desc'>('desc');
  const [jobPage, setJobPage] = React.useState(1);
  const [jobPageSize, setJobPageSize] = React.useState(20);
  const [totalJobExecLogs, setTotalJobExecLogs] = React.useState(0);
  const [fallbackItemResults, setFallbackItemResults] = React.useState<any[] | null>(null);
  const [isLoadingItemResults, setIsLoadingItemResults] = React.useState(false);
  const [itemsPage, setItemsPage] = React.useState(1);
  const [itemStatusFilter, setItemStatusFilter] = React.useState<string>('all');
  const itemsPageSize = 20;

  React.useEffect(() => {
    setPage(1);
  }, [statusFilter, typeFilter, showArchived, sortColumn, sortDirection, pageSize]);

  // Fetch Sync Logs with filters, sorting, and pagination
  const { data: syncLogs, isLoading, isError: isSyncLogsError } = useQuery({
    queryKey: ['sync-logs', statusFilter, typeFilter, sortColumn, sortDirection, page, pageSize, showArchived],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const lookbackIso = new Date(
        Date.now() - SYNC_LOGS_LOOKBACK_DAYS * 24 * 60 * 60 * 1000,
      ).toISOString();

      let query = supabase
        .from('sync_logs')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('archived', showArchived)
        .gte('start_time', lookbackIso);
      if (statusFilter) query = query.eq('status', statusFilter);
      if (typeFilter) query = query.eq('type', typeFilter);
      query = query.order(sortColumn, { ascending: sortDirection === 'asc' });

      const from = (page - 1) * pageSize;
      const to = from + pageSize; // fetch one extra row to detect a next page
      query = query.range(from, to);

      const { data, error } = await query;
      if (error) throw error;

      const rows = (data || []) as SyncLog[];
      setHasMoreSyncLogs(rows.length > pageSize);
      return rows.slice(0, pageSize);
    },
  });

  // Fetch job execution logs with filters, sorting, and pagination
  const { data: jobExecLogs, isLoading: isJobExecLoading } = useQuery({
    queryKey: ['job-execution-logs', jobStatusFilter, jobTypeFilter, jobSortColumn, jobSortDirection, jobPage, jobPageSize],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      let query = supabase
        .from('job_execution_logs')
        .select('*', { count: 'exact' })
        .eq('user_id', session.user.id);
      if (jobStatusFilter) query = query.eq('status', jobStatusFilter);
      if (jobTypeFilter) query = query.eq('type', jobTypeFilter);
      query = query.order(jobSortColumn, { ascending: jobSortDirection === 'asc' });
      query = query.range((jobPage - 1) * jobPageSize, jobPage * jobPageSize - 1);
      const { data, error, count } = await query;
      if (error) throw error;
      setTotalJobExecLogs(count ?? 0);
      return data as JobExecutionLog[];
    },
  });

  const archiveLog = async (logId: string) => {
    try {
      const { error } = await supabase
        .from('sync_logs')
        .update({ archived: true })
        .eq('id', logId);

      if (error) throw error;
      
      // Refresh the logs
      queryClient.invalidateQueries({ queryKey: ['sync-logs'] });
      
      // Clear selection if archived log was selected
      if (selectedLog?.id === logId) {
        setSelectedLog(null);
      }
    } catch (error) {
      console.error('Failed to archive log:', error);
      toast.error('Failed to archive log');
    }
  };

  const getStatusIcon = (status: SyncLog['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'skipped':
        return <Clock className="w-5 h-5 text-gray-500" />;
      case 'running':
        return <Clock className="w-5 h-5 text-blue-500 animate-spin" />;
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return format(date, 'MMM d, yyyy HH:mm:ss');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const getStatusClass = (status: SyncLog['status']) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'skipped':
        return 'bg-gray-100 text-gray-800';
      case 'running':
        return 'bg-blue-100 text-blue-800';
    }
  };

  // Fallback: for legacy logs without item_results, try to infer processed items from the time window
  React.useEffect(() => {
    setItemsPage(1); // reset pagination on selection change
    let cancelled = false;
    setFallbackItemResults(null);
    (async () => {
      const log: any = selectedLog as any;
      if (!log) return;
      const hasItems = Array.isArray(log?.details?.item_results) && (log.details.item_results?.length ?? 0) > 0;
      const supported = ['appointments', 'patients', 'quotes'].includes(log?.type);
      if (hasItems || !supported || !log?.start_time || !log?.end_time) return;
      try {
        setIsLoadingItemResults(true);
        const session = (await supabase.auth.getSession()).data.session;
        if (!session) return;
        let select = '';
        let table = log.type;
        if (log.type === 'appointments') {
          select = 'id, external_id, patient_id, start_time, zonedm_sync_status, zonedm_sync_error, last_sync';
        } else if (log.type === 'patients') {
          select = 'id, external_id, first_name, last_name, email, phone_primary, zonedm_sync_status, zonedm_sync_error, last_sync';
        } else if (log.type === 'quotes') {
          // Try both old and new status columns
          select = 'id, external_id, patient_id, appointment_external_id, modified_date, zonedm_sync_status, sync_status, last_sync';
        }
        const { data, error } = await supabase
          .from(table)
          .select(select)
          .eq('user_id', session.user.id)
          .gte('last_sync', log.start_time)
          .lte('last_sync', log.end_time)
          .order('last_sync', { ascending: false })
          .limit(400);
        if (cancelled) return;
        if (!error && Array.isArray(data)) {
          let mapped: any[] = [];
          if (log.type === 'appointments') {
            mapped = data.map((a: any) => ({
              id: a.id,
              external_id: a.external_id,
              status: a.zonedm_sync_status || 'unknown',
              error: a.zonedm_sync_error || undefined,
              appointment_date: a.start_time,
              patient_id: a.patient_id || null,
            }));
          } else if (log.type === 'patients') {
            mapped = data.map((p: any) => ({
              id: p.id,
              external_id: p.external_id,
              status: p.zonedm_sync_status || 'unknown',
              error: p.zonedm_sync_error || undefined,
              patient_name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || null,
              email: p.email || null,
              phone: p.phone_primary || null,
            }));
          } else if (log.type === 'quotes') {
            mapped = data.map((q: any) => ({
              id: q.id,
              external_id: q.external_id,
              status: q.zonedm_sync_status || q.sync_status || 'unknown',
              appointment_date: q.modified_date || null,
              patient_id: q.patient_id || null,
            }));
          }
          setFallbackItemResults(mapped);
        }
      } catch {
        // best-effort fallback
      } finally {
        if (!cancelled) setIsLoadingItemResults(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLog?.id]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sync Logs</h1>
          <div className="mt-1 flex items-center space-x-4">
            <p className="text-sm text-gray-500">
              View sync history and monitor data synchronization (last {SYNC_LOGS_LOOKBACK_DAYS} days)
            </p>
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              {showArchived ? (
                <>
                  <EyeOff className="w-4 h-4 mr-1" />
                  Hide Archived
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-1" />
                  Show Archived
                </>
              )}
            </button>
            {/* Filters for Sync Logs */}
            <select value={statusFilter ?? ''} onChange={e => setStatusFilter(e.target.value || null)} className="ml-4 border rounded px-2 py-1 text-sm">
              <option value="">All Statuses</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
              <option value="skipped">Skipped</option>
              <option value="running">Running</option>
            </select>
            <select value={typeFilter ?? ''} onChange={e => setTypeFilter(e.target.value || null)} className="ml-2 border rounded px-2 py-1 text-sm">
              <option value="">All Types</option>
              <option value="metadata">Master Data</option>
              <option value="patients">Patients</option>
              <option value="appointments">Appointments</option>
              <option value="providers">Providers</option>
              <option value="staff">Staff</option>
              <option value="procedures">Procedures</option>
              <option value="procedure_links">Procedure Links</option>
              <option value="charges">Charges</option>
              <option value="quotes">Quotes</option>
              <option value="facilities">Facilities</option>
              <option value="appointment_types">Appointment Types</option>
              <option value="appointment_subtypes">Appointment Subtypes</option>
              <option value="hl_calendars">HL Calendars</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sync Logs List */}
        <div className="lg:col-span-2">
          <Card>
            <div className="overflow-x-auto">
              {isLoading ? (
                <p className="p-4 text-gray-500">Loading sync logs...</p>
              ) : isSyncLogsError ? (
                <p className="p-4 text-red-600">Failed to load sync logs. Try again in a moment.</p>
              ) : syncLogs && syncLogs.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => { setSortColumn('status'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>
                      Status {sortColumn === 'status' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => { setSortColumn('type'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>
                      Type {sortColumn === 'type' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => { setSortColumn('direction'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>
                      Direction {sortColumn === 'direction' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => { setSortColumn('start_time'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>
                      Time {sortColumn === 'start_time' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Results
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {syncLogs?.map((log) => (
                    <tr
                      key={log.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedLog(log)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(log.status)}
                          <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(log.status)}`}>
                            {log.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{log.type}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{log.direction}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {formatDate(log.start_time)}
                          {log.details?.duration_ms && (
                            <span className="text-xs text-gray-500 ml-2">
                              ({Math.round(log.details.duration_ms / 1000)}s)
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(log.details || log.total_count !== null) && (
                          <span className="text-sm text-gray-900">
                            {log.details?.successCount ?? log.success_count ?? 0}/{log.details?.totalCount ?? log.total_count ?? 0}
                            {log.details?.errorCount ?? log.error_count ? ` (${log.details?.errorCount ?? log.error_count} errors)` : ''}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {!log.archived && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              archiveLog(log.id);
                            }}
                            className="text-gray-400 hover:text-gray-600"
                            title="Archive Log"
                          >
                            <Archive className="w-5 h-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              ) : (
                <p className="p-4 text-gray-500">No sync logs found in the last {SYNC_LOGS_LOOKBACK_DAYS} days.</p>
              )}
            </div>
            {/* Pagination for Sync Logs */}
            <div className="flex items-center justify-between mt-2 px-2 pb-2">
              <div>
                <button onClick={() => setPage(page - 1)} disabled={page === 1} className="px-2 py-1 border rounded mr-2">Previous</button>
                <span>Page {page}</span>
                <button onClick={() => setPage(page + 1)} disabled={!hasMoreSyncLogs} className="px-2 py-1 border rounded ml-2">Next</button>
              </div>
              <div>
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="border rounded px-2 py-1">
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="ml-2 text-xs text-gray-500">per page · last {SYNC_LOGS_LOOKBACK_DAYS} days</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Log Details */}
        <div className="lg:col-span-1">
          <Card className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Log Details</h3>
            {selectedLog ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <div className="mt-1 flex items-center">
                    {getStatusIcon(selectedLog.status)}
                    <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(selectedLog.status)}`}>
                      {selectedLog.status}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.type}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Direction</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.direction}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Time</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedLog.start_time)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">End Time</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedLog.end_time)}
                  </p>
                </div>

                {selectedLog.details?.duration_ms && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Duration</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {Math.round(selectedLog.details.duration_ms / 1000)} seconds
                    </p>
                  </div>
                )}

                {(selectedLog.details || selectedLog.total_count !== null) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Results</label>
                    <div className="mt-1 space-y-2">
                      <p className="text-sm text-gray-900">
                        Total: {selectedLog.details?.totalCount ?? selectedLog.total_count ?? 0}
                      </p>
                      <p className="text-sm text-green-600">
                        Success: {selectedLog.details?.successCount ?? selectedLog.success_count ?? 0}
                      </p>
                      <p className="text-sm text-red-600">
                        Errors: {selectedLog.details?.errorCount ?? selectedLog.error_count ?? 0}
                      </p>
                    </div>
                    {/* Item-level results table */}
                    {selectedLog.details && (() => {
                      // Prefer item_results from the log; fall back to inferred items for legacy logs
                      const allItems = (selectedLog.details as any).item_results ?? fallbackItemResults;
                      const hasContactCols = selectedLog.type === 'patients' || (Array.isArray(allItems) && allItems.some((item: any) => item?.email || item?.phone));
                      const uniqueStatuses = Array.isArray(allItems) ? Array.from(new Set((allItems as any[]).map((i: any) => i?.status).filter(Boolean))) : [];
                      const filtered = Array.isArray(allItems) ? (itemStatusFilter === 'all' ? allItems : (allItems as any[]).filter((i: any) => i?.status === itemStatusFilter)) : [];
                      const totalItems = Array.isArray(filtered) ? filtered.length : 0;
                      const startIdx = (itemsPage - 1) * itemsPageSize;
                      const paged = Array.isArray(filtered) ? filtered.slice(startIdx, startIdx + itemsPageSize) : [];
                      const exportCsv = () => {
                        const rows = filtered.map((r: any) => ({
                          patient_name: r.patient_name ?? '',
                          appointment_date: r.appointment_date ? new Date(r.appointment_date).toISOString() : '',
                          external_id: r.external_id ?? '',
                          email: r.email ?? '',
                          phone: r.phone ?? '',
                          status: r.status ?? '',
                          error: r.error ?? r.message ?? ''
                        }));
                        const headers = ['patient_name','appointment_date','external_id','email','phone','status','error'];
                        const escape = (v: any) => {
                          const s = String(v ?? '');
                          if (s.includes('"') || s.includes(',') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
                          return s;
                        };
                        const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape((r as any)[h])).join(','))].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${selectedLog.type}-${selectedLog.direction}-${(selectedLog.start_time || '').replace(/[:\\s]/g,'_')}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      };
                      return totalItems > 0 ? (
                      <div className="mt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Processed Records ({totalItems})</label>
                            <select
                              className="border rounded px-1 py-0.5 text-xs"
                              value={itemStatusFilter}
                              onChange={(e) => { setItemsPage(1); setItemStatusFilter(e.target.value || 'all'); }}
                            >
                              <option value="all">All statuses</option>
                              {uniqueStatuses.map((s) => (<option key={s} value={s}>{s}</option>))}
                            </select>
                            <button onClick={exportCsv} className="text-xs px-2 py-0.5 border rounded hover:bg-gray-50">Export CSV</button>
                          </div>
                          {totalItems > itemsPageSize && (
                            <div className="text-xs text-gray-600 space-x-2">
                              <button className="px-2 py-0.5 border rounded disabled:opacity-50" disabled={itemsPage === 1} onClick={() => setItemsPage(p => Math.max(1, p - 1))}>Prev</button>
                              <span>Page {itemsPage} / {Math.ceil(totalItems / itemsPageSize)}</span>
                              <button className="px-2 py-0.5 border rounded disabled:opacity-50" disabled={itemsPage * itemsPageSize >= totalItems} onClick={() => setItemsPage(p => p + 1)}>Next</button>
                            </div>
                          )}
                        </div>
                        <div className="max-h-96 overflow-y-auto border rounded">
                          <table className="min-w-full text-xs">
                            <thead className="bg-gray-100 sticky top-0">
                              <tr>
                                <th className="px-2 py-1 text-left font-semibold">Patient Name</th>
                                {selectedLog.type === 'appointments' && (
                                  <th className="px-2 py-1 text-left font-semibold">Appointment Date</th>
                                )}
                                <th className="px-2 py-1 text-left font-semibold">External ID</th>
                                {hasContactCols && (
                                  <>
                                    <th className="px-2 py-1 text-left font-semibold">Email</th>
                                    <th className="px-2 py-1 text-left font-semibold">Phone</th>
                                  </>
                                )}
                                <th className="px-2 py-1 text-left font-semibold">Status</th>
                                <th className="px-2 py-1 text-left font-semibold">Error/Message</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paged.map((item: any, idx: number) => (
                                <tr key={idx} className="border-t hover:bg-gray-50">
                                  <td className="px-2 py-1 font-medium">
                                    {item.patient_name || <span className="text-gray-400 italic">N/A</span>}
                                  </td>
                                  {selectedLog.type === 'appointments' && (
                                    <td className="px-2 py-1 text-gray-600">
                                      {item.appointment_date ? new Date(item.appointment_date).toLocaleString() : <span className="text-gray-400">N/A</span>}
                                    </td>
                                  )}
                                  <td className="px-2 py-1 font-mono text-gray-700">{item.external_id || <span className="text-gray-400">N/A</span>}</td>
                                  {(selectedLog.type === 'patients' || item.email || item.phone) && selectedLog.details && (
                                    <>
                                      <td className="px-2 py-1 text-gray-600">{item.email || <span className="text-gray-400">-</span>}</td>
                                      <td className="px-2 py-1 text-gray-600">{item.phone || <span className="text-gray-400">-</span>}</td>
                                    </>
                                  )}
                                  <td className="px-2 py-1">
                                    <span className={
                                      item.status === 'success' || item.status === 'zonedm_synced' ? 'text-green-600 font-medium' :
                                      item.status === 'warning' || item.status === 'waiting_for_patient' ? 'text-yellow-600 font-medium' :
                                      'text-red-600 font-medium'
                                    }>
                                      {item.status}
                                    </span>
                                  </td>
                                  <td className="px-2 py-1 text-xs text-gray-700">
                                    {item.error || item.message || <span className="text-gray-400">-</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      ) : (
                        <div className="mt-2 text-xs text-gray-500">
                          {isLoadingItemResults
                            ? 'Loading item details...'
                            : 'No item details recorded for this run.'}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {selectedLog.error && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Error</label>
                    <p className="mt-1 text-sm text-red-600">{selectedLog.error}</p>
                  </div>
                )}

                {selectedLog.status === 'skipped' && selectedLog.details?.reason && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Skip Reason</label>
                    <p className="mt-1 text-sm text-gray-600">
                      {selectedLog.details.reason === 'disabled' ? 'Job is disabled' : 
                       selectedLog.details.reason === 'scheduled' ? 'Scheduled for future execution' : 
                       selectedLog.details.reason || 'Unknown reason'}
                    </p>
                    {selectedLog.details.message && (
                      <p className="mt-1 text-xs text-gray-500">{selectedLog.details.message}</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Select a log to view details</p>
            )}
          </Card>
        </div>
      </div>

      {/* Job Execution History Section */}
      <div className="mt-10">
        <h2 className="text-xl font-bold mb-2">Job Execution History</h2>
        {/* Filters for Job Execution Logs */}
        <div className="mb-2 flex items-center space-x-2">
          <select value={jobStatusFilter ?? ''} onChange={e => setJobStatusFilter(e.target.value || null)} className="border rounded px-2 py-1 text-sm">
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="started">Started</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
          <select value={jobTypeFilter ?? ''} onChange={e => setJobTypeFilter(e.target.value || null)} className="border rounded px-2 py-1 text-sm">
            <option value="">All Types</option>
            <option value="metadata">Metadata</option>
            <option value="appointments">Appointments</option>
            <option value="patients">Patients</option>
            <option value="quotes">Quotes</option>
          </select>
        </div>
        <Card>
          <div className="overflow-x-auto">
            {isJobExecLoading ? (
              <p className="p-4 text-gray-500">Loading...</p>
            ) : jobExecLogs && jobExecLogs.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left cursor-pointer" onClick={() => { setJobSortColumn('type'); setJobSortDirection(jobSortDirection === 'asc' ? 'desc' : 'asc'); }}>
                      Type {jobSortColumn === 'type' ? (jobSortDirection === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="px-4 py-2 text-left cursor-pointer" onClick={() => { setJobSortColumn('status'); setJobSortDirection(jobSortDirection === 'asc' ? 'desc' : 'asc'); }}>
                      Status {jobSortColumn === 'status' ? (jobSortDirection === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="px-4 py-2 text-left cursor-pointer" onClick={() => { setJobSortColumn('start_time'); setJobSortDirection(jobSortDirection === 'asc' ? 'desc' : 'asc'); }}>
                      Started {jobSortColumn === 'start_time' ? (jobSortDirection === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="px-4 py-2 text-left cursor-pointer" onClick={() => { setJobSortColumn('end_time'); setJobSortDirection(jobSortDirection === 'asc' ? 'desc' : 'asc'); }}>
                      Completed {jobSortColumn === 'end_time' ? (jobSortDirection === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="px-4 py-2 text-left">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {jobExecLogs.map((log) => (
                    <tr key={log.id} className="border-t">
                      <td className="px-4 py-2">{log.type}</td>
                      <td className="px-4 py-2">
                        <span className={
                          log.status === 'completed' ? 'text-green-600' :
                          log.status === 'started' ? 'text-blue-600' :
                          log.status === 'scheduled' ? 'text-gray-600' :
                          'text-red-600'
                        }>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-4 py-2">{formatDate(log.start_time)}</td>
                      <td className="px-4 py-2">{formatDate(log.end_time)}</td>
                      <td className="px-4 py-2 text-xs text-red-600 max-w-xs truncate" title={log.error ?? undefined}>{log.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="p-4 text-gray-500">No job executions found.</p>
            )}
          </div>
          {/* Pagination for Job Execution Logs */}
          <div className="flex items-center justify-between mt-2">
            <div>
              <button onClick={() => setJobPage(jobPage - 1)} disabled={jobPage === 1} className="px-2 py-1 border rounded mr-2">Previous</button>
              <span>Page {jobPage}</span>
              <button onClick={() => setJobPage(jobPage + 1)} disabled={jobPage * jobPageSize >= totalJobExecLogs} className="px-2 py-1 border rounded ml-2">Next</button>
            </div>
            <div>
              <select value={jobPageSize} onChange={e => { setJobPageSize(Number(e.target.value)); setJobPage(1); }} className="border rounded px-2 py-1">
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="ml-2 text-xs text-gray-500">of {totalJobExecLogs} jobs</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}